import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import React from "react";

import { useAuth } from "@/providers";
import { RobotoButton } from "@/shared/components";
import { FoxgloveUserConfig } from "@/shared/domain/integrations";
import { useBeginWebhookIntegration } from "@/shared/domain/integrations/hooks/useBeginWebhookIntegration";

import styles from "./FoxgloveIntegrationStepper.module.css";

type DatasetCreationMode = "recording" | "event";

export function ProvisionWebhookContent() {
  const { currentOrganization } = useAuth();
  const beginWebhookMutation = useBeginWebhookIntegration();
  const [datasetMode, setDatasetMode] =
    React.useState<DatasetCreationMode>("recording");
  const [filterInstantaneous, setFilterInstantaneous] = React.useState(false);

  const handleProvisionWebhook = () => {
    if (!currentOrganization?.org_id) {
      return;
    }

    const webhookEvents =
      datasetMode === "recording"
        ? ["recording.imported"]
        : ["recording.imported", "event.created"];

    const userConfig: FoxgloveUserConfig | null =
      datasetMode === "event"
        ? {
            foxglove_events: {
              filter_instantaneous: filterInstantaneous,
            },
          }
        : null;

    beginWebhookMutation.mutate({
      orgId: currentOrganization.org_id,
      webhookProvider: "foxglove",
      webhookEvents,
      userConfig,
    });
  };

  return (
    <>
      <Typography variant="body2" className={styles.marginBottom2}>
        First, we need to create a webhook endpoint that Foxglove can send data
        to.
      </Typography>

      <FormControl component="fieldset" className={styles.marginBottom2}>
        <FormLabel>
          <Typography variant="body2" className={styles.marginBottom1}>
            How would you like to organize Foxglove recordings in Roboto?
          </Typography>
        </FormLabel>
        <RadioGroup
          value={datasetMode}
          onChange={(e) =>
            setDatasetMode(e.target.value as DatasetCreationMode)
          }
        >
          <FormControlLabel
            value="recording"
            control={<Radio />}
            label={
              <Typography variant="body2">
                Create a dataset for each recording
              </Typography>
            }
          />
          <FormControlLabel
            value="event"
            control={<Radio />}
            label={
              <Typography variant="body2">
                Group recordings by matching Foxglove device event
              </Typography>
            }
          />
        </RadioGroup>
      </FormControl>

      {datasetMode === "event" && (
        <Box className={styles.marginBottom2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filterInstantaneous}
                onChange={(e) => setFilterInstantaneous(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                Ignore instantaneous events (where start time equals end time)
              </Typography>
            }
          />
        </Box>
      )}

      <Box className={styles.marginBottom2}>
        <RobotoButton
          variant="contained"
          onClick={handleProvisionWebhook}
          disabled={beginWebhookMutation.isPending}
          startIcon={
            beginWebhookMutation.isPending ? (
              <CircularProgress size={16} />
            ) : null
          }
          eventName="ProvisionFoxgloveWebhookClicked"
        >
          {beginWebhookMutation.isPending
            ? "Provisioning..."
            : "Provision Webhook"}
        </RobotoButton>
      </Box>

      {beginWebhookMutation.isError && (
        <Alert severity="error" className={styles.marginBottom2}>
          Failed to provision webhook. Please try again.
        </Alert>
      )}
    </>
  );
}
